import Tooltip from 'components/controls/Tooltip';
import EpdSelectComponent from 'components/form/EpdSelectComponent';
import HelpIcon from 'components/icons/HelpIcon';
import AddNewMemberRow from 'components/member/AddNewMemberRow';
import { confirmAlert } from 'components/v2/confirm-dialog/confirmAlert';
import { ValidationIconWithContainer } from 'components/v2/form/styled';
import { toaster } from 'components/v2/toast';
import EpdDeveloperSelect from 'components/wizard/EpdDeveloperSelect';
import { EpdVerifierSelect } from 'components/wizard/EpdVerifierSelector';
import WizardPcrSelectorSection from 'components/wizard/WizardPcrSelectorSection';
import WizardVersionHistoryDialog from 'components/v2/wizard/dialogs/WizardVersionHistoryDialog';
import WizardCommentLogButton from 'components/v2/wizard/WizardCommentLogButton';
import { Role } from 'constants/constants';
import { CompanyContext } from 'contexts/CompanyContextProvider';
import useCurrentUser from 'hooks/useCurrentUser';
import { Button } from 'primereact/button';
import React, { useContext } from 'react';
import { useTranslation } from 'react-i18next';
import { MultiValue, SingleValue } from 'react-select';
import { toast } from 'react-toastify';
import AuthService from 'services/AuthService';
import {
  ActionState,
  EPDModel,
  EPDStatusDisplayName,
  EPDVerifierModel,
  MembershipModel,
  PCRModel,
  VerificationType,
  VerifierType,
} from 'services/EpdClient';
import styled, { css, useTheme } from 'styled-components';
import { ButtonSmall, HyperLink, InfoBox, Input } from 'styles/Styles.styled';
import { CheckboxLabel } from 'styles/v2/Styles.styled';
import { Option } from 'types/types';
import * as Yup from 'yup';
import { Validation } from './Validation';
import WizardStep1Tools from './sections/WizardStep1Tools';
import { epdSelectStyles, FixedLabel, WizardStep1Row } from './styles';
import { WizardStepValidationSchema } from './types';
import { generateRegistrationCertificate, getEPD } from 'services/api/epdApi';
import { HelpBox, HelpBoxBoldText } from 'components/v2/help-boxes';
import EpdRoutes from 'routes/EpdRoutes';

type UserOption = Option & { email: string };

const WizardStep1: React.FunctionComponent<{
  epd?: EPDModel;
  epdDevelopers: MembershipModel[];
  verifiers: EPDVerifierModel[];
  isLoadingVerifiers: boolean;
  onChangePcr?: any;
  onChangeCpcr?: any;
  onRemovePcr?: any;
  onRemoveCpcr?: any;
  onChangeEpd?: any;
  setUpdatedEpd?: (epd: EPDModel) => void;
  refetchVerifiers?: () => void;
  refetchPractitioners?: () => void;
  validation: WizardStepValidationSchema;
}> = ({
  epd,
  epdDevelopers,
  verifiers,
  isLoadingVerifiers,
  onChangePcr,
  onChangeCpcr,
  onRemovePcr,
  onRemoveCpcr,
  onChangeEpd,
  setUpdatedEpd,
  refetchVerifiers,
  refetchPractitioners,
  validation,
}) => {
  const { companyId, company } = useContext(CompanyContext);
  const { t } = useTranslation();
  const theme = useTheme();
  const [openLcaPractitioners, setOpenLcaPractitioners] = React.useState<boolean>(false);
  const [openVerifier, setOpenVerifier] = React.useState<boolean>(false);
  const [isGeneratingCertificate, setIsGeneratingCertificate] = React.useState<boolean>(false);
  const userInfo = useCurrentUser();
  const isAdmin = AuthService.isAdmin();
  const isLicenseeAdmin = AuthService.isLicenseeAdmin();
  const isLicenseeAdminInReview =
    isLicenseeAdmin && epd?.epdState?.statusDisplayName === EPDStatusDisplayName.LicenseeReview;
  const isOwner = AuthService.isCompanyOwner(companyId as string);
  const hasOnlyLcaPractitionerCompanyRole = AuthService.hasOnlyLcaPractitionerCompanyRole(companyId as string);
  const selectedVerifierUser =
    epd?.epdVerifier && ({ label: epd.epdVerifier.displayName, value: epd.epdVerifier.id } as Option);
  let isValidVerifier = true;

  const accreditedCertificationBodyAddress = epd?.epdVerifier?.companyAddress
    ? [
        epd.epdVerifier.companyAddress.country,
        epd.epdVerifier.companyAddress.city,
        epd.epdVerifier.companyAddress.zipCode,
        epd.epdVerifier.companyAddress.addressLine,
      ]
        .filter((value: string | null | undefined) => !!value && value.trim() !== '')
        .join(', ')
    : '';
  try {
    validation.validateSyncAt('epdVerifier', epd);
  } catch (e) {
    const validationError = e as Yup.ValidationError;
    isValidVerifier = validationError.type !== 'excluded-from-company';
  }

  const selectedContactPersonUser =
    epd?.contactPerson &&
    ({
      label: epd.contactPerson.contactName,
      value: epd.contactPerson.contactUserId,
      email: epd.contactPerson.contactEmail,
    } as UserOption);

  const changeEpdVerifier = (verifier: SingleValue<Option>) => {
    const handleVerifierChange = (verifier: SingleValue<Option>) => {
      onChangeEpd('EPDVerifier', verifier?.value || undefined);
    };

    if (!isValidVerifier) {
      confirmAlert({
        caption: t('epdWizard.wizardStep1.properties.epdVerifier.confirmModal.title'),
        description: t('epdWizard.wizardStep1.properties.epdVerifier.confirmModal.description'),
        acceptLabel: t('Submit'),
        rejectLabel: t('Cancel'),
        onConfirm: () => {
          handleVerifierChange(verifier);
        },
      });
    } else {
      handleVerifierChange(verifier);
    }
  };

  const changeEpdContactPerson = (contactPerson: SingleValue<UserOption>) => {
    if (contactPerson?.value) {
      onChangeEpd('EPDContactPerson', contactPerson.value);
    } else {
      onChangeEpd('EPDContactPerson', undefined);
    }
  };

  const changePcr = (pcr: PCRModel | undefined) => {
    onChangePcr?.(pcr);
  };

  const changeCpcr = (cpcr: PCRModel | undefined) => {
    onChangeCpcr?.(cpcr);
  };

  const removePcr = () => {
    onRemovePcr?.();
  };

  const removeCpcr = () => {
    onRemoveCpcr?.();
  };

  const changeEpdDevelopers = (developers: MultiValue<Option>) => {
    if (Array.isArray(developers)) {
      const selectedEpdDevelopers = epdDevelopers
        .filter((x) => developers.map((x) => x.value).includes(x.userId))
        .map((x) => x.id);

      const isUserLCAPractitioner = epdDevelopers
        .filter((x) => developers.map((x) => x.value).includes(x.userId))
        .find((developer) => developer.userId === userInfo.id);

      if (!isAdmin && !isLicenseeAdmin && !isOwner && !isUserLCAPractitioner) {
        toast.error(t('epdWizard.wizardStep1.properties.epdDeveloper.messages.restrictEPDDeveloperDeleteOwnEntry'), {
          position: 'top-center',
        });
      } else {
        onChangeEpd('EPDDevelopers', selectedEpdDevelopers);
      }
    } else {
      if (isAdmin || isLicenseeAdmin || isOwner) {
        onChangeEpd('EPDDevelopers', undefined);
      } else {
        toast.error(t('epdWizard.wizardStep1.properties.epdDeveloper.messages.restrictEPDDeveloperDeleteOwnEntry'), {
          position: 'top-center',
        });
      }
    }
  };

  const changeVerificationType = (event: React.ChangeEvent<HTMLInputElement>) => {
    onChangeEpd('VerificationType', event.currentTarget.value);
  };

  const handleVerifierButtonClick = () => {
    setOpenVerifier(!openVerifier);
  };

  if (!epd) {
    return null;
  }

  const customVerifierSelectorStyles = {
    menu: (styles: any) => ({ ...styles }),
    control: (styles: any) => ({
      ...styles,
      borderRadius: 0,
      borderColor: 'gray',
      color: 'black',
      fontSize: '14px',
      minHeight: '49px',
      margin: '0.5rem 0rem',
    }),
    singleValue: (styles: any, state: any) => ({
      ...styles,
      color: state.isDisabled ? theme.colors.elementDisabledTextColor : 'black',
    }),
    multiValueLabel: (styles: any) => ({
      ...styles,
      fontSize: '100%',
    }),
  };

  const selectedUsers =
    epd?.epdDevelopers &&
    epd?.epdDevelopers.map((x) => {
      return { label: x.contactName, value: x.contactUserId } as Option;
    });

  const handleCopyContactPersonEmailToClipboard = () => {
    if (selectedContactPersonUser && selectedContactPersonUser.value) {
      navigator.clipboard
        .writeText(selectedContactPersonUser.email)
        .then(() => {} /* ask UI if some notification needed */)
        .catch((error) => {
          console.error('Failed to copy email to clipboard:', error);
        });
    }
  };

  const handleGenerateRegistrationCertificate = async () => {
    if (isGeneratingCertificate || !epd.id) {
      return;
    }
    setIsGeneratingCertificate(true);
    try {
      await generateRegistrationCertificate(epd.id);

      if (setUpdatedEpd && epd.id) {
        const updatedEpd = await getEPD(epd.id);
        setUpdatedEpd(updatedEpd);
      }

      toaster({
        severity: 'success',
        summary: 'Registration certificate has been generated successfully!',
        details: (
          <>
            It is now available on the EPD details page at&nbsp;
            <HyperLink href="https://www.environdec.com/library" target="_blank" rel="noopener noreferrer">
              environdec.com
            </HyperLink>
            .
          </>
        ),
      });
    } catch (error: any) {
      toaster({
        severity: 'error',
        summary: 'Something went wrong while generating your certificate.',
        details: 'Please try again or contact support if the problem continues.',
      });
    } finally {
      setIsGeneratingCertificate(false);
    }
  };

  const epdVerifierContactUserId = epd?.epdVerifier?.contactUserId;
  const isGenerateRegistrationCertificateButtonDisabled =
    isGeneratingCertificate || epd.generateRegistrationCertificateState === ActionState.Disabled;
  const isGenerateRegistrationCertificateBlockHidden = epd.generateRegistrationCertificateState === ActionState.Hidden;

  return (
    <Container>
      <WizardStep1Row state={epd.dataSetIdState.actionState}>
        <FixedLabel>
          {t('epdWizard.wizardStep1.properties.uuidDataSet')}{' '}
          <HelpIcon content={t('epdWizard.wizardStep1.tooltips.uuidDataSet')} />
        </FixedLabel>
        <em>{epd.dataSetId}</em>
      </WizardStep1Row>
      {!isGenerateRegistrationCertificateBlockHidden && (
        <GenerateCertificateRow>
          <RegistrationCertificateLabel state={epd.generateRegistrationCertificateState}>
            {t('epdWizard.wizardStep1.properties.registrationCertificate')}
          </RegistrationCertificateLabel>
          <div style={{ marginLeft: '5px' }}>
            <InfoBox>
              <div>
                <div style={{ marginRight: '1rem', paddingLeft: '0.25rem', alignSelf: 'flex-start' }}></div>
                <div>
                  You can automatically generate a registration certificate for the <b>currently published EPD version</b>.
                  An EPD update or new version is <b>not</b> required for this.
                </div>
                <div className="mt-2">
                  The registration certificate will be accessible on the EPD details page at&nbsp;
                  <HyperLink href={'https://www.environdec.com/library'} target="_blank" rel="noopener noreferrer">
                    environdec.com
                  </HyperLink>
                  .
                </div>
              </div>
            </InfoBox>

            <div style={{ width: 'fit-content' }}>
              <GenerateCertificateButton
                onClick={handleGenerateRegistrationCertificate}
                disabled={isGenerateRegistrationCertificateButtonDisabled}
              >
                {isGeneratingCertificate ? 'Generating certificate...' : 'Generate certificate'}
              </GenerateCertificateButton>
              {epd.generateRegistrationCertificateState === ActionState.Disabled && !isGeneratingCertificate && (
                <Tooltip position="right" offset={5}>
                  A certificate already exists for the currently published EPD version. Another one can be generated only
                  after publishing a new EPD version.
                </Tooltip>
              )}
            </div>
          </div>
        </GenerateCertificateRow>
      )}
      <WizardStep1Row state={epd.titleState.actionState}>
        <FixedLabel>
          {t('epdWizard.wizardStep1.properties.title')} <HelpIcon content={t('epdWizard.wizardStep1.tooltips.title')} />{' '}
          <Validation validation={validation} field="title" epd={epd} />
        </FixedLabel>
        <>
          <Input
            defaultValue={epd.title}
            disabled={epd.titleState.actionState === ActionState.Disabled}
            onBlur={(e: React.ChangeEvent<HTMLInputElement>) =>
              e.currentTarget.value !== epd.title && onChangeEpd('title', e.currentTarget.value)
            }
          />
          {epd.id && (
            <div style={{ display: 'flex', alignItems: 'center' }}>
              <WizardVersionHistoryDialog epdId={epd.id} />
              <WizardCommentLogButton epdId={epd.id} />
            </div>
          )}
        </>
      </WizardStep1Row>

      <WizardStep1Row state={epd.titleState.actionState}>
        <FixedLabel style={{ margin: '0.5rem 1rem 1.5rem 0' }}>
          {t('epdWizard.wizardStep1.properties.contact.title')}{' '}
          <HelpIcon content={t('epdWizard.wizardStep1.properties.contact.tooltip')} />{' '}
          <Validation validation={validation} field="contactPerson" epd={epd} />
        </FixedLabel>
        <div style={{ display: 'flex', flexDirection: 'column' }}>
          <EpdSelectComponent
            placeholder={t('placeholder.defaultSelect')}
            isDisabled={epd.titleState.actionState === ActionState.Disabled}
            isMulti={false}
            styles={epdSelectStyles}
            value={selectedContactPersonUser}
            onChange={(e: UserOption) => changeEpdContactPerson(e)}
            options={epdDevelopers.map((user) => {
              return {
                value: user.userId,
                label: `${user.userName} (${user.roleName})`,
                email: user.userEmail,
              } as UserOption;
            })}
          />
          <CheckboxWrapper state={epd?.contactPersonState?.actionState}>
            <StyledCheckbox
              name="sentEmailsToContactPersonFlag"
              type="checkbox"
              style={{ margin: '0' }}
              disabled={epd?.contactPersonState?.actionState === ActionState.Disabled}
              defaultChecked={epd?.sentEmailsToContactPersonFlag ?? false}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                onChangeEpd('sentEmailsToContactPersonFlag', e.currentTarget.checked)
              }
            />
            <StyledCheckboxLabel htmlFor="sentEmailsToContactPersonFlag">
              Send EPD e-mail notifications to contact person
            </StyledCheckboxLabel>
          </CheckboxWrapper>
        </div>
        <Button
          text
          className="p-2"
          style={{ margin: '0.5rem 0rem 1.5rem 0rem' }}
          onClick={handleCopyContactPersonEmailToClipboard}
        >
          <i className="pi pi-copy" style={{ fontSize: '1.5rem' }} />
          <Tooltip position="title">Copy Email</Tooltip>
        </Button>
      </WizardStep1Row>

      <WizardStep1Row state={epd.epdDevelopersState.actionState}>
        <FixedLabel>
          {t('epdWizard.wizardStep1.properties.epdDeveloper.lcaPractitioner')}{' '}
          <HelpIcon content={t('epdWizard.wizardStep1.tooltips.lcaPractitioner')} />{' '}
          <ValidationIconWithContainer>
            <Validation validation={validation} field="epdDevelopers" epd={epd} />
          </ValidationIconWithContainer>
        </FixedLabel>
        <EpdSelectComponent
          placeholder={t('placeholder.defaultSelect')}
          isDisabled={epd.epdDevelopersState.actionState === ActionState.Disabled}
          isMulti={true}
          styles={epdSelectStyles}
          value={selectedUsers}
          onChange={(e: MultiValue<Option>) => changeEpdDevelopers(e)}
          options={epdDevelopers.map((user) => {
            return {
              value: user.userId,
              label: `${user.userName} (${user.roleName})`,
            } as Option;
          })}
        />
        {epd.epdDevelopersAddOrInviteState.actionState === ActionState.Enabled && (
          <ButtonSmall
            style={{ maxWidth: '8rem' }}
            onClick={() => setOpenLcaPractitioners(!openLcaPractitioners)}
            title={openLcaPractitioners ? '' : t('epdWizard.wizardStep1.properties.epdDeveloper.epdDeveloperMenu')}
          >
            {openLcaPractitioners
              ? t('epdWizard.wizardStep1.epdDeveloperMenu.close')
              : t('epdWizard.wizardStep1.properties.epdDeveloper.addInvite')}
          </ButtonSmall>
        )}
      </WizardStep1Row>
      {openLcaPractitioners && (
        <AccordionContent>
          <SelectDeveloperContainer>
            <LcaDialogHelpBox>
              <div>
                You can add LCA Practitioners to your project in two ways:
                <ul>
                  <li>
                    If you have already agreed to work with a practitioner from the list below, click{' '}
                    <b>"Add practitioner"</b> to assign them directly to your EPD.
                  </li>
                  <li>
                    If you need to discuss collaboration first, select one or more practitioners and click{' '}
                    <b>"Request collaboration"</b>&nbsp; to notify them of your interest in collaborating on LCA/EPD
                    development. Once confirmed, use <b>"Add practitioner"</b>&nbsp; as described above.
                  </li>
                </ul>
              </div>
            </LcaDialogHelpBox>
            <div>
              <EpdDeveloperSelect
                epd={epd}
                onSelected={(updatedEpd) => setUpdatedEpd?.(updatedEpd)}
                refetchPractitioners={refetchPractitioners}
              />
            </div>
          </SelectDeveloperContainer>
          <InviteContainer>
            <p>{t('epdWizard.wizardStep1.epdDeveloperMenu.inviteEpdDeveloper')}</p>
            <AddNewMemberRow
              buttonText={t('epdWizard.wizardStep1.epdDeveloperMenu.inviteDeveloper')}
              companyId={companyId || ''}
              roleId={Role.EPDDeveloper}
              hideGuestOptions
            />
          </InviteContainer>
        </AccordionContent>
      )}
      <WizardStep1Row state={epd?.verificationTypeState?.actionState}>
        <FixedLabel style={{ alignSelf: 'start', marginTop: 0 }}>
          {t('epdWizard.wizardStep1.properties.verificationType.title')}
          <HelpIcon content="EPD must be independently verified before registration and publication. The EPD Owner can manage the verification types available within the organisation." />
        </FixedLabel>
        <div style={{ display: 'flex', flexDirection: 'column' }}>
          {company?.hasEPDProcessCertification && (
            <>
              <label style={{ color: hasOnlyLcaPractitionerCompanyRole ? 'gray' : 'inherit' }}>
                <input
                  disabled={
                    epd?.verificationTypeState?.actionState === ActionState.Disabled ||
                    hasOnlyLcaPractitionerCompanyRole ||
                    isLicenseeAdminInReview
                  }
                  type="radio"
                  id="epdprocesscertification"
                  name="verification"
                  value={VerificationType.EPDProcessCertification}
                  checked={epd?.verificationType === VerificationType.EPDProcessCertification}
                  onChange={(e) => {
                    epd?.verificationTypeState?.actionState === ActionState.Enabled && changeVerificationType(e);
                  }}
                />
                {t('epdWizard.wizardStep1.properties.verificationType.children.epdProcessCertification')}
              </label>
              <br />
            </>
          )}
          <label style={{ color: hasOnlyLcaPractitionerCompanyRole ? 'gray' : 'inherit' }}>
            <input
              disabled={
                epd?.verificationTypeState?.actionState === ActionState.Disabled ||
                hasOnlyLcaPractitionerCompanyRole ||
                isLicenseeAdminInReview
              }
              type="radio"
              id="epdverification"
              name="verification"
              value={VerificationType.EPDVerification}
              checked={epd?.verificationType === VerificationType.EPDVerification}
              onChange={(e) => {
                epd?.verificationTypeState?.actionState === ActionState.Enabled && changeVerificationType(e);
              }}
            />
            {t('epdWizard.wizardStep1.properties.verificationType.children.epdVerification')}
          </label>
          {company?.hasToolVerificationType && (
            <>
              <br />
              <label style={{ color: hasOnlyLcaPractitionerCompanyRole ? 'gray' : 'inherit' }}>
                <input
                  disabled={
                    epd?.verificationTypeState?.actionState === ActionState.Disabled ||
                    hasOnlyLcaPractitionerCompanyRole ||
                    isLicenseeAdminInReview
                  }
                  type="radio"
                  id="preVerifiedTool"
                  name="preVerifiedTool"
                  value={VerificationType.EPDPreverifiedTool}
                  checked={epd?.verificationType === VerificationType.EPDPreverifiedTool}
                  onChange={(e) => {
                    epd?.verificationTypeState?.actionState === ActionState.Enabled && changeVerificationType(e);
                  }}
                />
                Pre-verified & Integrated tool
              </label>
            </>
          )}
        </div>
      </WizardStep1Row>

      {epd && <WizardStep1Tools epd={epd} validation={validation} onChangeEpd={onChangeEpd} />}

      <WizardStep1Row state={epd?.epdVerifierState?.actionState}>
        <FixedVerifierLabelWithParam isValidVerifier={isValidVerifier}>
          {t('epdWizard.wizardStep1.properties.epdVerifier.title')}
          <HelpIcon content={t('epdWizard.wizardStep1.tooltips.epdVerifier')} />{' '}
          <ValidationIconWithContainer>
            <Validation validation={validation} field="epdVerifier" epd={epd} />
          </ValidationIconWithContainer>
        </FixedVerifierLabelWithParam>
        <div>
          <EpdSelectComponent
            placeholder={t('placeholder.defaultSelect')}
            isDisabled={epd?.epdVerifierState?.actionState === ActionState.Disabled || isLicenseeAdminInReview}
            onChange={(e: SingleValue<Option>) => changeEpdVerifier(e)}
            value={selectedVerifierUser}
            isMulti={false}
            styles={customVerifierSelectorStyles}
            isClearable
            options={verifiers.map((verifier) => {
              return {
                value: verifier.id,
                label: verifier.displayName,
              } as Option;
            })}
          />
        </div>
        {epd?.epdVerifierState?.actionState === ActionState.Enabled && !isLicenseeAdminInReview && (
          <ButtonSmallMaxWidth
            onClick={handleVerifierButtonClick}
            title={openVerifier ? '' : t('epdWizard.wizardStep1.properties.epdVerifier.epdVerifierMenu')}
          >
            {openVerifier
              ? t('epdWizard.wizardStep1.epdVerifierMenu.close')
              : t('epdWizard.wizardStep1.properties.epdVerifier.findMore')}
          </ButtonSmallMaxWidth>
        )}
        {epdVerifierContactUserId &&
          !isLoadingVerifiers &&
          !verifiers.some((v) => v.contactUserId === epdVerifierContactUserId) && (
            <NotAvailableVerifier>{t('epdWizard.wizardStep1.properties.epdVerifier.notAvailable')}</NotAvailableVerifier>
          )}
      </WizardStep1Row>
      {openVerifier && (
        <AccordionContent style={{ padding: '0 0 0 1rem' }}>
          <div style={{ flex: '1 1 100%' }}>
            <HelpBox>
              <span>
                Visit the{' '}
                <HyperLink target="_blank" rel="noopener noreferrer" href={EpdRoutes.resources.contactVerifier}>
                  Contact verifier
                </HyperLink>{' '}
                page to browse approved verifiers and send an email with the required collaboration details to the selected
                verifier(s).
              </span>
              <span>
                After confirmation, use the <HelpBoxBoldText>"Add verifier"</HelpBoxBoldText> button here to assign the
                verifier to your EPD.
              </span>
            </HelpBox>
            <p>{t('epdWizard.wizardStep1.epdVerifierMenu.needVerifier')}</p>
            {epd && (
              <EpdVerifierSelect
                epd={epd}
                isValidVerifier={isValidVerifier}
                setUpdatedEpd={setUpdatedEpd}
                refetchVerifiers={refetchVerifiers}
              />
            )}
          </div>
        </AccordionContent>
      )}

      <WizardStep1Row
        state={
          epd?.epdVerifier?.verifierType === VerifierType.Body ? epd?.epdVerifierState?.actionState : ActionState.Hidden
        }
      >
        <FixedLabel>Accredited certification body Address</FixedLabel>
        <Input disabled name="address" value={accreditedCertificationBodyAddress} />
      </WizardStep1Row>

      <WizardPcrSelectorSection
        // TODO: clarify download pcr files logic
        epd={epd}
        onChangePcr={changePcr}
        onChangeCpcr={changeCpcr}
        onRemovePcr={removePcr}
        onRemoveCpcr={removeCpcr}
        onChangeEpd={onChangeEpd}
        validation={validation}
      />

      <WizardStep1Row state={epd?.isSectorState.actionState}>
        <FixedLabel style={{ flex: '0 0 175px' }}>
          {t('epdWizard.wizardStep1.properties.sectorEpd')}{' '}
          <HelpIcon content={t('epdWizard.wizardStep1.tooltips.sectorEpd')} />
        </FixedLabel>
        <input
          style={{ justifySelf: 'flex-start' }}
          type="checkbox"
          defaultChecked={epd?.isSectorEPD ?? false}
          disabled={epd?.isSectorState.actionState === ActionState.Disabled}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => onChangeEpd('isSectorEpd', e.currentTarget.checked)}
        />
      </WizardStep1Row>
    </Container>
  );
};

const GenerateCertificateRow = styled(WizardStep1Row)`
  grid-template-rows: 2;
  grid-column-gap: 0px;
  grid-row-gap: 0px;
  justify-content: left;

  grid-template-columns: 100%;

  ${(props) => props.theme.media.desktop} {
    grid-template-columns: 2fr 5fr;
  }
`;

const RegistrationCertificateLabel = styled(FixedLabel)<{ state?: ActionState }>`
  ${(props) => props.theme.media.desktop} {
    margin: 0.5rem 1rem 3.5rem 0;
  }

  ${(props) =>
    props.state === ActionState.Disabled &&
    css`
      color: gray;
    `}
`;

const GenerateCertificateButton = styled(ButtonSmall)`
  width: fit-content;
  &:disabled {
    background: ${(props) => props.theme.colors.elementsButtonDisabled};
  }
`;

const Container = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  grid-row-gap: 1.5rem;
`;

const NotAvailableVerifier = styled.div`
  ${(props) => props.theme.media.desktop} {
    grid-column: 2 / 3;
  }
  font-size: 14px;
  background-color: #f3f4fb;
  padding-top: 0.75rem;
  padding-bottom: 0.75rem;
  padding-left: 1rem;
  padding-right: 1rem;
  color: #151515b2;
`;

const SelectDeveloperContainer = styled.div`
  margin-left: 1rem;
`;

const AccordionContent = styled.div`
  border-left: solid 3px ${(props) => props.theme.colors.regionColorLight};
`;

const InviteContainer = styled(SelectDeveloperContainer)`
  > form {
    flex-direction: column;
  }

  button {
    margin-top: 0.5rem;
  }
`;

const ButtonSmallMaxWidth = styled(ButtonSmall)`
  max-width: 8rem;
`;

const StyledCheckboxLabel = styled(CheckboxLabel)`
  font-family: 'Roboto';
  font-style: normal;
  font-weight: 400;
  font-size: 14px;
  line-height: 16px;
  color: ${(props) => props.theme.colors.elementDisabledTextColor};
  margin-left: 8px;
`;

const StyledCheckbox = styled.input.attrs({ type: 'checkbox' })`
  width: 16px;
  height: 16px;
  cursor: pointer;

  &:checked {
    accent-color: ${(props) => props.theme.colors.primaryOrange}; /* Works in most modern browsers */
  }
`;

const LcaDialogHelpBox = styled(HelpBox)`
  ul {
    margin: 0;
    padding-left: 30px;
  }
`;

const CheckboxWrapper = styled.div<{ state?: ActionState }>`
  display: ${(props) => (props?.state === ActionState.Hidden ? 'none' : 'flex')};
  align-items: center;

  ${(props) =>
    props.state === ActionState.Disabled &&
    css`
      label {
        color: ${(props) => props.theme.colors.elementDisabledTextColor};
      }
    `}
`;

const FixedVerifierLabelWithParam = styled(FixedLabel)<{ isValidVerifier: boolean }>`
  ${(props) => props.theme.media.desktop} {
    margin: ${(props) => (!props.isValidVerifier ? '0 0 3rem 0' : '0')};
  }
`;

export default WizardStep1;
